/** News view component. */

import { fetchNewsData } from '@/api/services/news';
import { showToast } from '@/utils/helpers';
import { logError } from '@/utils/errors';
import { formatTimestamp, escapeHTML } from '@/utils/formatters';
import { hideAllViews, setActiveNavLink, showView } from '@/utils/viewNavigation';
import type { NewsItem } from '@/types/responses';

let currentNewsItems: NewsItem[] = [];
let sortColumn: string | null = null;
let sortDirection: 'asc' | 'desc' = 'desc';
let filterType: string | null = null;
let searchQuery: string = '';
const expandedNewsUrls = new Set<string>();

/**
 * Refresh news data without closing expanded rows or flashing.
 */
export async function refreshNewsData(): Promise<void> {
  try {
    const data = await fetchNewsData();
    if (!data || !data.news_items) {
      if (import.meta.env.DEV) {
        console.warn('Failed to fetch news data for refresh');
      }
      return;
    }
    
    // Save which rows are currently expanded (by URL as unique identifier)
    const expandedRows = new Set<string>(expandedNewsUrls);
    
    document.querySelectorAll('tr[id^="news-expanded-"]').forEach(row => {
      if (!row.classList.contains('hidden')) {
        const expandedId = row.id;
        const index = expandedId.replace('news-expanded-', '');
        const rowElement = document.getElementById(`news-row-${index}`);
        
        if (rowElement) {
          // Find the corresponding news item by looking at the ticker and headline
          const tickerCell = rowElement.querySelector('td:nth-child(2) span[data-ticker]');
          const headlineCell = rowElement.querySelector('td:nth-child(4) a');
          
          if (tickerCell && headlineCell) {
            const url = headlineCell.getAttribute('href');
            if (url) {
              expandedRows.add(url);
            }
          }
        }
      }
    });
    
    // Update current items
    currentNewsItems = data.news_items;
    expandedNewsUrls.clear();
    expandedRows.forEach((url) => expandedNewsUrls.add(url));
    
    // Update table in place without replacing entire HTML
    updateNewsTableInPlace(currentNewsItems, expandedRows);
    
  } catch (error) {
    logError(error, 'NewsRefresh');
    console.error('Error refreshing news data:', error);
  }
}

export async function renderNews(): Promise<void> {
  try {
    // 1. Show view and skeleton immediately (no waiting for API)
    hideAllViews();
    showView('news');
    import('@/stores/navigation').then(module => {
      module.useNavigationStore.getState().setCurrentView('news');
    });
    setActiveNavLink('news');

    const container = document.getElementById('news-table-container');
    if (container) {
      container.innerHTML = `
        <div class="py-5 space-y-3">
          <p class="text-secondary text-sm">Loading SEC filings & news...</p>
          <div class="h-10 rounded bg-gray-700 animate-pulse"></div>
          <div class="h-10 rounded bg-gray-700 animate-pulse"></div>
          <div class="h-10 rounded bg-gray-700 animate-pulse"></div>
          <div class="h-10 rounded bg-gray-700 animate-pulse"></div>
          <div class="h-10 rounded bg-gray-700 animate-pulse"></div>
          <div class="h-10 rounded bg-gray-700 animate-pulse"></div>
        </div>
      `;
    }

    // 2. Fetch while skeleton is visible
    const data = await fetchNewsData();
    if (!data || !data.news_items) {
      if (container) {
        container.innerHTML = '<p class="text-red-400">Failed to load news data. Please retry.</p>';
      }
      showToast('Failed to fetch news data', true);
      return;
    }

    currentNewsItems = data.news_items;
    renderNewsTable(currentNewsItems);
  } catch (error) {
    logError(error, 'News');
    const container = document.getElementById('news-table-container');
    if (container) {
      container.innerHTML = '<p class="text-red-400">Failed to load news data. Please retry.</p>';
    }
    showToast('Failed to load news', true);
  }
}

function getFilingTypeColor(filingType: string | null): string {
  if (!filingType) return 'bg-gray-600';
  
  const type = filingType.toUpperCase();
  if (type.includes('8-K')) return 'bg-red-600';
  if (type.includes('10-K')) return 'bg-blue-600';
  if (type.includes('10-Q')) return 'bg-green-600';
  if (type.includes('S-1') || type.includes('S-3')) return 'bg-purple-600';
  return 'bg-gray-600';
}

function getFilingTypeBadge(filingType: string | null): string {
  if (!filingType || filingType === 'N/A') return '';
  const color = getFilingTypeColor(filingType);
  return `<span class="px-2 py-1 rounded text-xs font-semibold ${color} text-white">${escapeHTML(filingType)}</span>`;
}

function getPriceStatusBadge(status: string | undefined): string {
  const normalized = (status || 'warming').toLowerCase();
  if (normalized === 'fresh') return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-green-700 text-white">Fresh</span>';
  if (normalized === 'stale') return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-yellow-700 text-white">Stale</span>';
  if (normalized === 'unsupported_symbol') return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-purple-700 text-white">Unsupported</span>';
  if (normalized === 'provider_no_data') return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-gray-600 text-white">Unavailable</span>';
  if (normalized === 'error') return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-red-700 text-white">Error</span>';
  return '<span class="text-[10px] px-1.5 py-0.5 rounded bg-blue-700 text-white">Warming</span>';
}

function formatPrice(
  price: number | null,
  dayChange: number | null,
  priceStatus: string | undefined,
  priceSource: string | null | undefined
): string {
  const statusBadge = getPriceStatusBadge(priceStatus);
  const sourceText = priceSource ? `<span class="text-[10px] text-secondary">${escapeHTML(priceSource)}</span>` : '';
  if (price === null || price === undefined) {
    return `<div class="flex flex-col items-end gap-1"><span class="text-gray-500">N/A</span>${statusBadge}${sourceText}</div>`;
  }
  
  const priceStr = `$${price.toFixed(2)}`;
  
  if (dayChange === null || dayChange === undefined) {
    return `<div class="flex flex-col items-end gap-1"><span class="text-white">${priceStr}</span>${statusBadge}${sourceText}</div>`;
  }
  
  const changeColor = dayChange >= 0 ? 'text-green-400' : 'text-red-400';
  const changeSymbol = dayChange >= 0 ? '+' : '';
  const changeStr = `${changeSymbol}${dayChange.toFixed(2)}%`;
  
  return `
    <div class="flex flex-col items-end gap-1">
      <span class="text-white">${priceStr}</span>
      <span class="text-xs ${changeColor}">${changeStr}</span>
      ${statusBadge}
      ${sourceText}
    </div>
  `;
}

interface FilingMetadata {
  filedDate: string | null;
  accNo: string | null;
  size: string | null;
  items: string[];
}

function parseFilingMetadata(contentSummary: string | null): FilingMetadata {
  const metadata: FilingMetadata = {
    filedDate: null,
    accNo: null,
    size: null,
    items: []
  };
  
  if (!contentSummary) return metadata;
  
  // Parse Filed date
  const filedMatch = contentSummary.match(/<b>Filed:<\/b>\s*([^<]+)/i);
  if (filedMatch) {
    metadata.filedDate = filedMatch[1].trim();
  }
  
  // Parse AccNo
  const accNoMatch = contentSummary.match(/<b>AccNo:<\/b>\s*([^<]+)/i);
  if (accNoMatch) {
    metadata.accNo = accNoMatch[1].trim();
  }
  
  // Parse Size
  const sizeMatch = contentSummary.match(/<b>Size:<\/b>\s*([^<]+)/i);
  if (sizeMatch) {
    metadata.size = sizeMatch[1].trim();
  }
  
  // Parse Item sections (Item X.XX: Description)
  const itemMatches = contentSummary.matchAll(/Item\s+(\d+\.\d+):\s*([^<]+)/gi);
  for (const match of itemMatches) {
    metadata.items.push(`${match[1]}: ${match[2].trim()}`);
  }
  
  return metadata;
}

function getRelatedFilings(ticker: string, currentUrl: string, allItems: NewsItem[]): NewsItem[] {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  return allItems
    .filter(item => 
      item.ticker === ticker && 
      item.url !== currentUrl &&
      item.filing_date && 
      new Date(item.filing_date) >= sevenDaysAgo
    )
    .sort((a, b) => {
      const dateA = a.filing_date ? new Date(a.filing_date).getTime() : 0;
      const dateB = b.filing_date ? new Date(b.filing_date).getTime() : 0;
      return dateB - dateA; // Most recent first
    })
    .slice(0, 5); // Limit to 5 most recent
}

function renderFilingDetailsPanel(item: NewsItem, _index: number, allItems: NewsItem[]): string {
  const metadata = parseFilingMetadata(item.content_summary);
  const relatedFilings = getRelatedFilings(item.ticker, item.url, allItems);
  
  return `
    <div class="space-y-4">
      <!-- Filing Metadata -->
      <div class="bg-gray-900 rounded p-3 border border-gray-700">
        <h4 class="font-semibold text-neon-cyan mb-3">Filing Details</h4>
        <div class="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span class="text-secondary">Filing Type:</span>
            <span class="ml-2 text-white">${escapeHTML(item.filing_type || 'N/A')}</span>
          </div>
          <div>
            <span class="text-secondary">Filing Date:</span>
            <span class="ml-2 text-white">${item.filing_date ? formatTimestamp(item.filing_date) : 'N/A'}</span>
          </div>
          ${metadata.filedDate ? `
          <div>
            <span class="text-secondary">Filed:</span>
            <span class="ml-2 text-white">${escapeHTML(metadata.filedDate)}</span>
          </div>
          ` : ''}
          ${metadata.accNo ? `
          <div>
            <span class="text-secondary">Accession No:</span>
            <span class="ml-2 text-white font-mono text-xs">${escapeHTML(metadata.accNo)}</span>
          </div>
          ` : ''}
          ${metadata.size ? `
          <div>
            <span class="text-secondary">File Size:</span>
            <span class="ml-2 text-white">${escapeHTML(metadata.size)}</span>
          </div>
          ` : ''}
          <div>
            <span class="text-secondary">Significance Score:</span>
            <span class="ml-2 font-semibold ${item.significance_score >= 7 ? 'text-green-400' : item.significance_score >= 4 ? 'text-yellow-400' : 'text-gray-400'}">
              ${item.significance_score.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
      
      ${metadata.items.length > 0 ? `
      <!-- Filing Items -->
      <div class="bg-gray-900 rounded p-3 border border-gray-700">
        <h4 class="font-semibold text-neon-cyan mb-3">Filing Items</h4>
        <ul class="space-y-1 text-sm">
          ${metadata.items.map(item => `
            <li class="text-secondary">
              <span class="text-neon-cyan font-mono">${escapeHTML(item.split(':')[0])}:</span>
              <span class="ml-2 text-white">${escapeHTML(item.split(':').slice(1).join(':').trim())}</span>
            </li>
          `).join('')}
        </ul>
      </div>
      ` : ''}
      
      ${item.content_summary && item.content_summary.trim().length > 0 ? `
      <!-- Full Content Summary -->
      <div class="bg-gray-900 rounded p-3 border border-gray-700">
        <h4 class="font-semibold text-neon-cyan mb-3">Content Summary</h4>
        <div class="text-secondary text-sm leading-relaxed max-h-64 overflow-y-auto">
          ${item.content_summary.replace(/<b>/gi, '<b class="text-white">')}
        </div>
      </div>
      ` : ''}
      
      ${relatedFilings.length > 0 ? `
      <!-- Related Filings -->
      <div class="bg-gray-900 rounded p-3 border border-gray-700">
        <h4 class="font-semibold text-neon-cyan mb-3">Related Filings (Last 7 Days)</h4>
        <div class="space-y-2">
          ${relatedFilings.map(related => `
            <div class="flex items-center justify-between p-2 bg-gray-800 rounded hover:bg-gray-750">
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  ${getFilingTypeBadge(related.filing_type)}
                  <span class="text-sm text-white">${escapeHTML(related.headline || 'No headline')}</span>
                </div>
                <div class="text-xs text-secondary mt-1">
                  ${related.filing_date ? formatTimestamp(related.filing_date) : 'N/A'}
                </div>
              </div>
              <a 
                href="${escapeHTML(related.url)}" 
                target="_blank" 
                class="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs text-white ml-2"
                rel="noopener noreferrer"
              >
                View →
              </a>
            </div>
          `).join('')}
        </div>
      </div>
      ` : ''}
      
      <!-- Actions -->
      <div class="flex gap-2">
        <a 
          href="${escapeHTML(item.url)}" 
          target="_blank" 
          class="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm text-white"
          rel="noopener noreferrer"
        >
          View Full Filing on SEC EDGAR →
        </a>
      </div>
    </div>
  `;
}

/**
 * Update table rows in place without replacing entire HTML (prevents flash).
 */
function updateNewsTableInPlace(
  newsItems: NewsItem[],
  expandedRows: Set<string>
): void {
  const tbody = document.querySelector('#news-table-container tbody');
  if (!tbody) {
    // If tbody doesn't exist, fall back to full render
    renderNewsTable(newsItems);
    return;
  }
  
  // Apply filters and sorting (same logic as renderNewsTable)
  let filteredItems = [...newsItems];
  
  if (filterType && filterType !== 'all') {
    filteredItems = filteredItems.filter(item => 
      filterType && item.filing_type?.toUpperCase().includes(filterType.toUpperCase())
    );
  }
  
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredItems = filteredItems.filter(item =>
      item.ticker.toLowerCase().includes(query) ||
      item.headline?.toLowerCase().includes(query) ||
      item.content_summary?.toLowerCase().includes(query)
    );
  }
  
  if (sortColumn) {
    filteredItems.sort((a, b) => {
      let aVal: any;
      let bVal: any;
      
      switch (sortColumn) {
        case 'date':
          aVal = a.filing_date ? new Date(a.filing_date).getTime() : 0;
          bVal = b.filing_date ? new Date(b.filing_date).getTime() : 0;
          break;
        case 'ticker':
          aVal = a.ticker.toLowerCase();
          bVal = b.ticker.toLowerCase();
          break;
        case 'score':
          aVal = a.significance_score;
          bVal = b.significance_score;
          break;
        case 'type':
          aVal = a.filing_type || '';
          bVal = b.filing_type || '';
          break;
        case 'price':
          aVal = a.price ?? -Infinity;
          bVal = b.price ?? -Infinity;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }
  
  // Create a map of existing rows by URL for quick lookup
  const existingRows = new Map<string, { row: HTMLTableRowElement; expandedRow: HTMLTableRowElement | null }>();
  tbody.querySelectorAll('tr[id^="news-row-"]').forEach(row => {
    const headlineCell = row.querySelector('td:nth-child(4) a');
    if (headlineCell) {
      const url = headlineCell.getAttribute('href');
      if (url) {
        const index = row.id.replace('news-row-', '');
        const expandedRow = document.getElementById(`news-expanded-${index}`) as HTMLTableRowElement | null;
        existingRows.set(url, { row: row as HTMLTableRowElement, expandedRow });
      }
    }
  });
  
  // Remove rows that no longer exist
  const currentUrls = new Set(filteredItems.map(item => item.url));
  existingRows.forEach((rowData, url) => {
    if (!currentUrls.has(url)) {
      rowData.row.remove();
      if (rowData.expandedRow) {
        rowData.expandedRow.remove();
      }
    }
  });
  
  // Use DocumentFragment to batch DOM updates and minimize flash
  const fragment = document.createDocumentFragment();
  
  // Remove all existing rows first (batch removal)
  const rowsToRemove: HTMLTableRowElement[] = [];
  tbody.querySelectorAll('tr').forEach(tr => {
    rowsToRemove.push(tr as HTMLTableRowElement);
  });
  
  // Create all new rows in fragment first
  const newRows: Array<{ row: HTMLTableRowElement; expandedRow: HTMLTableRowElement; wasExpanded: boolean }> = [];
  
  filteredItems.forEach((item, index) => {
    const newRow = createNewsRow(item, index);
    const newExpandedRow = createNewsExpandedRow(item, index);
    const wasExpanded = expandedRows.has(item.url);
    
    if (wasExpanded) {
      newExpandedRow.classList.remove('hidden');
      const expandBtn = newRow.querySelector('.expand-row-btn') as HTMLElement;
      if (expandBtn) {
        expandBtn.textContent = 'Collapse';
      }
    }
    
    newRows.push({ row: newRow, expandedRow: newExpandedRow, wasExpanded });
  });
  
  // Batch remove old rows
  rowsToRemove.forEach(row => row.remove());
  
  // Batch append new rows using fragment
  newRows.forEach(({ row, expandedRow }) => {
    fragment.appendChild(row);
    fragment.appendChild(expandedRow);
  });
  
  // Single append operation - minimizes flash
  tbody.appendChild(fragment);
  
  // Re-setup event handlers for new rows
  setupNewsEventHandlers();
}

function createNewsRow(item: NewsItem, index: number): HTMLTableRowElement {
  const rowId = `news-row-${index}`;
  
  const row = document.createElement('tr');
  row.id = rowId;
  row.className = 'border-b border-primary hover:bg-gray-700';
  
  row.innerHTML = `
    <td class="text-left p-2">${item.filing_date ? formatTimestamp(item.filing_date) : 'N/A'}</td>
    <td class="text-left p-2">
      <span class="font-bold text-neon-cyan cursor-pointer hover:underline" data-ticker="${escapeHTML(item.ticker)}">
        ${escapeHTML(item.ticker)}
      </span>
    </td>
    <td class="text-left p-2">${getFilingTypeBadge(item.filing_type)}</td>
    <td class="text-left p-2">
      <a href="${escapeHTML(item.url)}" target="_blank" class="text-blue-400 hover:underline" rel="noopener noreferrer">
        ${escapeHTML((item.headline || 'No headline').substring(0, 60))}${(item.headline || '').length > 60 ? '...' : ''}
      </a>
    </td>
    <td class="text-right p-2">
      <span class="font-semibold ${item.significance_score >= 7 ? 'text-green-400' : item.significance_score >= 4 ? 'text-yellow-400' : 'text-gray-400'}">
        ${item.significance_score.toFixed(2)}
      </span>
    </td>
    <td class="text-right p-2">
      ${formatPrice(item.price, item.day_change, item.price_status, item.price_source)}
    </td>
    <td class="text-center p-2">
      <button 
        class="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs text-white expand-row-btn"
        data-index="${index}"
        data-row-id="${rowId}"
      >
        View
      </button>
    </td>
  `;
  
  return row;
}


function createNewsExpandedRow(item: NewsItem, index: number): HTMLTableRowElement {
  const expandedId = `news-expanded-${index}`;
  
  const row = document.createElement('tr');
  row.id = expandedId;
  row.className = 'hidden border-b border-primary bg-gray-800';
  
  row.innerHTML = `
    <td colspan="7" class="p-4">
      ${renderFilingDetailsPanel(item, index, currentNewsItems)}
    </td>
  `;
  
  return row;
}

function renderNewsTable(newsItems: NewsItem[]): void {
  const container = document.getElementById('news-table-container');
  if (!container) return;
  
  // Apply filters
  let filteredItems = [...newsItems];
  
  if (filterType && filterType !== 'all') {
    filteredItems = filteredItems.filter(item => 
      filterType && item.filing_type?.toUpperCase().includes(filterType.toUpperCase())
    );
  }
  
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredItems = filteredItems.filter(item =>
      item.ticker.toLowerCase().includes(query) ||
      item.headline?.toLowerCase().includes(query) ||
      item.content_summary?.toLowerCase().includes(query)
    );
  }
  
  // Apply sorting
  if (sortColumn) {
    filteredItems.sort((a, b) => {
      let aVal: any;
      let bVal: any;
      
      switch (sortColumn) {
        case 'date':
          aVal = a.filing_date ? new Date(a.filing_date).getTime() : 0;
          bVal = b.filing_date ? new Date(b.filing_date).getTime() : 0;
          break;
        case 'ticker':
          aVal = a.ticker.toLowerCase();
          bVal = b.ticker.toLowerCase();
          break;
        case 'score':
          aVal = a.significance_score;
          bVal = b.significance_score;
          break;
        case 'type':
          aVal = a.filing_type || '';
          bVal = b.filing_type || '';
          break;
        case 'price':
          aVal = a.price ?? -Infinity;
          bVal = b.price ?? -Infinity;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }
  
  // Get unique filing types for filter
  const filingTypes = Array.from(new Set(
    newsItems
      .map(item => item.filing_type)
      .filter((type): type is string => type !== null && type !== 'N/A')
  )).sort();
  
  const html = `
    <div class="mb-4 space-y-4">
      <!-- Filters and Search -->
      <div class="flex flex-wrap gap-4 items-center">
        <div class="flex items-center gap-2">
          <label class="text-sm text-secondary">Filter by Type:</label>
          <select id="news-type-filter" class="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm">
            <option value="all">All Types</option>
            ${filingTypes.map(type => `
              <option value="${escapeHTML(type)}" ${filterType === type ? 'selected' : ''}>${escapeHTML(type)}</option>
            `).join('')}
          </select>
        </div>
        <div class="flex items-center gap-2 flex-grow">
          <label class="text-sm text-secondary">Search:</label>
          <input 
            type="text" 
            id="news-search" 
            placeholder="Search by ticker, headline..." 
            value="${escapeHTML(searchQuery)}"
            class="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm flex-grow max-w-md"
          />
        </div>
      </div>
    </div>
    
    ${filteredItems.length === 0 ? `
      <div class="text-center py-8">
        <p class="text-secondary text-lg mb-2">No news items found</p>
        <p class="text-secondary text-sm">
          ${filterType || searchQuery 
            ? 'Try adjusting your filters or search terms.' 
            : 'No SEC filings or news items from the last 24 hours.'}
        </p>
      </div>
    ` : `
    <table class="w-full">
      <thead>
        <tr class="border-b border-primary">
          <th class="text-left p-2 cursor-pointer hover:bg-gray-700 select-none" data-column="date">
            Date ${sortColumn === 'date' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
          </th>
          <th class="text-left p-2 cursor-pointer hover:bg-gray-700 select-none" data-column="ticker">
            Ticker ${sortColumn === 'ticker' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
          </th>
          <th class="text-left p-2 cursor-pointer hover:bg-gray-700 select-none" data-column="type">
            Type ${sortColumn === 'type' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
          </th>
          <th class="text-left p-2">Headline</th>
          <th class="text-right p-2 cursor-pointer hover:bg-gray-700 select-none" data-column="score">
            Score ${sortColumn === 'score' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
          </th>
          <th class="text-right p-2 cursor-pointer hover:bg-gray-700 select-none" data-column="price">
            Price ${sortColumn === 'price' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
          </th>
          <th class="text-center p-2">Actions</th>
        </tr>
      </thead>
      <tbody>
        ${filteredItems.map((item, index) => {
          const rowId = `news-row-${index}`;
          const expandedId = `news-expanded-${index}`;
          
          return `
          <tr class="border-b border-primary hover:bg-gray-700" id="${rowId}">
            <td class="text-left p-2">${item.filing_date ? formatTimestamp(item.filing_date) : 'N/A'}</td>
            <td class="text-left p-2">
              <span class="font-bold text-neon-cyan cursor-pointer hover:underline" data-ticker="${escapeHTML(item.ticker)}">
                ${escapeHTML(item.ticker)}
              </span>
            </td>
            <td class="text-left p-2">${getFilingTypeBadge(item.filing_type)}</td>
            <td class="text-left p-2">
              <a href="${escapeHTML(item.url)}" target="_blank" class="text-blue-400 hover:underline" rel="noopener noreferrer">
                ${escapeHTML((item.headline || 'No headline').substring(0, 60))}${(item.headline || '').length > 60 ? '...' : ''}
              </a>
            </td>
            <td class="text-right p-2">
              <span class="font-semibold ${item.significance_score >= 7 ? 'text-green-400' : item.significance_score >= 4 ? 'text-yellow-400' : 'text-gray-400'}">
                ${item.significance_score.toFixed(2)}
              </span>
            </td>
            <td class="text-right p-2">
              ${formatPrice(item.price, item.day_change, item.price_status, item.price_source)}
            </td>
            <td class="text-center p-2">
              <button 
                class="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs text-white expand-row-btn"
                data-index="${index}"
                data-row-id="${rowId}"
              >
                ${expandedNewsUrls.has(item.url) ? 'Collapse' : 'View'}
              </button>
            </td>
          </tr>
          <tr id="${expandedId}" class="${expandedNewsUrls.has(item.url) ? '' : 'hidden'} border-b border-primary bg-gray-800" data-news-url="${escapeHTML(item.url)}">
            <td colspan="7" class="p-4">
              ${renderFilingDetailsPanel(item, index, newsItems)}
            </td>
          </tr>
          `;
        }).join('')}
      </tbody>
    </table>
    `}
  `;
  
  container.innerHTML = html;
  
  // Set up event handlers
  setupNewsEventHandlers();
}

function setupNewsEventHandlers(): void {
  // Filter by type
  const typeFilter = document.getElementById('news-type-filter');
  if (typeFilter) {
    typeFilter.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;
      filterType = target.value === 'all' ? null : target.value;
      renderNewsTable(currentNewsItems);
    });
  }
  
  // Search
  const searchInput = document.getElementById('news-search');
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      // Save cursor position before re-rendering
      const cursorPosition = target.selectionStart || 0;
      searchQuery = target.value;
      renderNewsTable(currentNewsItems);
      // Restore focus and cursor position after re-rendering
      setTimeout(() => {
        const newSearchInput = document.getElementById('news-search') as HTMLInputElement;
        if (newSearchInput) {
          newSearchInput.focus();
          newSearchInput.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    });
  }
  
  // Sortable column headers
  document.querySelectorAll('th[data-column]').forEach(header => {
    header.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const column = target.getAttribute('data-column');
      if (column) {
        if (sortColumn === column) {
          sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          sortColumn = column;
          sortDirection = 'desc';
        }
        renderNewsTable(currentNewsItems);
      }
    });
  });
  
  // Expand row buttons
  document.querySelectorAll('.expand-row-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const index = target.getAttribute('data-index');
      const rowId = target.getAttribute('data-row-id');
      
      if (index !== null && rowId) {
        const expandedId = `news-expanded-${index}`;
        const expandedRow = document.getElementById(expandedId);
        if (expandedRow) {
          const isHidden = expandedRow.classList.contains('hidden');
          const row = document.getElementById(rowId);
          const url = row?.querySelector('td:nth-child(4) a')?.getAttribute('href')
            || expandedRow.getAttribute('data-news-url');
          expandedRow.classList.toggle('hidden', !isHidden);
          if (url) {
            if (isHidden) {
              expandedNewsUrls.add(url);
            } else {
              expandedNewsUrls.delete(url);
            }
          }
          target.textContent = isHidden ? 'Collapse' : 'View';
        }
      }
    });
  });
  
}

